import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Typography,
  Alert,
  Skeleton,
  alpha,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import CancelIcon from '@mui/icons-material/Cancel';
import RestoreIcon from '@mui/icons-material/Restore';
import AddIcon from '@mui/icons-material/Add';
import PlaceIcon from '@mui/icons-material/Place';
import VideocamIcon from '@mui/icons-material/Videocam';
import { memo, useState } from 'react';
import type { Meeting } from '@penticton-dev-events/shared';

export type GroupMeetingsTabProps = {
  readonly meetings: readonly Meeting[];
  readonly loading: boolean;
  readonly error: Error | null;
  readonly isOrganizer: boolean;
  readonly onCreateMeeting?: () => void;
  readonly onMeetingClick?: (meeting: Meeting) => void;
  readonly onCancelMeeting?: (meetingId: string) => Promise<void>;
  readonly onRestoreMeeting?: (meetingId: string) => Promise<void>;
  readonly processingMeetingIds?: Set<string>;
};

type MeetingCardImageProps = {
  readonly meeting: Meeting;
};

const MeetingCardImage = ({ meeting }: MeetingCardImageProps) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  if (!meeting.posterUrl || error) {
    // Fallback gradient when no poster or error loading
    return (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          paddingTop: '56.25%', // 16:9 aspect ratio
          background: theme =>
            `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.8)} 0%, ${alpha(theme.palette.secondary.main, 0.6)} 100%)`,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 3,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: 'white',
              textAlign: 'center',
              fontWeight: 600,
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
            }}
          >
            {meeting.title}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        paddingTop: '56.25%', // 16:9 aspect ratio
        overflow: 'hidden',
        backgroundColor: 'grey.200',
      }}
    >
      {loading && (
        <Skeleton
          variant="rectangular"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          }}
        />
      )}
      <Box
        component="img"
        src={meeting.posterUrl}
        alt={meeting.posterAlt || `${meeting.title} poster`}
        onLoad={() => setLoading(false)}
        onError={() => {
          setLoading(false);
          setError(true);
        }}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: loading ? 0 : 1,
          transition: 'opacity 0.3s ease-in-out',
        }}
      />
      {/* Gradient overlay for better text readability */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '50%',
          background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)',
        }}
      />
      {/* Title overlay on image */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: 'white',
            fontWeight: 600,
            textShadow: '0 2px 8px rgba(0,0,0,0.5)',
          }}
        >
          {meeting.title}
        </Typography>
      </Box>
    </Box>
  );
};

const GroupMeetingsTab = memo<GroupMeetingsTabProps>(
  ({
    meetings,
    loading,
    error,
    isOrganizer,
    onCreateMeeting,
    onMeetingClick,
    onCancelMeeting,
    onRestoreMeeting,
    processingMeetingIds = new Set(),
  }) => {
    const upcomingMeetings = meetings.filter(
      meeting =>
        meeting.status === 'scheduled' && new Date(meeting.startsAt) > new Date(),
    );
    const pastMeetings = meetings.filter(
      meeting =>
        meeting.status === 'scheduled' && new Date(meeting.startsAt) <= new Date(),
    );
    const canceledMeetings = meetings.filter(meeting => meeting.status === 'canceled');

    const formatMeetingDate = (meeting: Meeting) => {
      const startDate = new Date(meeting.startsAt);
      const endDate = new Date(meeting.endsAt);

      return `${startDate.toLocaleDateString()} • ${startDate.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })} - ${endDate.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })}`;
    };

    const getMeetingStatusColor = (meeting: Meeting) => {
      if (meeting.status === 'canceled') return 'error';
      const now = new Date();
      const meetingStart = new Date(meeting.startsAt);
      return meetingStart > now ? 'success' : 'default';
    };

    const getMeetingStatusLabel = (meeting: Meeting) => {
      if (meeting.status === 'canceled') return 'Canceled';
      const now = new Date();
      const meetingStart = new Date(meeting.startsAt);
      return meetingStart > now ? 'Upcoming' : 'Past';
    };

    const renderMeetingCard = (meeting: Meeting) => (
      <Card
        key={meeting.id}
        sx={{
          flexBasis: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33% - 16px)' },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          opacity: meeting.status === 'canceled' ? 0.7 : 1,
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme => theme.shadows[8],
          },
        }}
      >
        <CardActionArea
          onClick={onMeetingClick ? () => onMeetingClick(meeting) : undefined}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            height: '100%',
          }}
        >
          <MeetingCardImage meeting={meeting} />

          <CardContent sx={{ flexGrow: 1 }}>
            <Stack spacing={1.5}>
              {/* Status and Event Type badges */}
              <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                <Chip
                  size="small"
                  label={getMeetingStatusLabel(meeting)}
                  color={getMeetingStatusColor(meeting)}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  icon={
                    meeting.eventType === 'in-person' ? (
                      <PlaceIcon />
                    ) : meeting.eventType === 'online' ? (
                      <VideocamIcon />
                    ) : undefined
                  }
                  label={
                    meeting.eventType === 'in-person'
                      ? 'In-Person'
                      : meeting.eventType === 'online'
                        ? 'Online'
                        : 'Hybrid'
                  }
                  color={
                    meeting.eventType === 'in-person'
                      ? 'info'
                      : meeting.eventType === 'online'
                        ? 'secondary'
                        : 'warning'
                  }
                  variant="outlined"
                />
              </Stack>

              {/* Date and Time */}
              <Typography variant="body2" color="text.secondary">
                <EventIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                {formatMeetingDate(meeting)}
              </Typography>

              {/* Location/Online info */}
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {(meeting.eventType === 'in-person' || meeting.eventType === 'hybrid') &&
                  meeting.location && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                    >
                      <PlaceIcon sx={{ fontSize: 14 }} />
                      {meeting.location.label}
                    </Typography>
                  )}
                {(meeting.eventType === 'online' || meeting.eventType === 'hybrid') &&
                  meeting.onlineUrl && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                    >
                      <VideocamIcon sx={{ fontSize: 14 }} />
                      Online Available
                    </Typography>
                  )}
              </Stack>

              {/* Description */}
              {meeting.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minHeight: '2.5em',
                  }}
                >
                  {meeting.description}
                </Typography>
              )}

              {/* Organizer action buttons */}
              {isOrganizer && (onCancelMeeting || onRestoreMeeting) && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  {meeting.status === 'scheduled' && onCancelMeeting && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      startIcon={<CancelIcon />}
                      onClick={e => {
                        e.stopPropagation();
                        void onCancelMeeting(meeting.id);
                      }}
                      disabled={processingMeetingIds.has(meeting.id)}
                      fullWidth
                    >
                      Cancel
                    </Button>
                  )}

                  {meeting.status === 'canceled' && onRestoreMeeting && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<RestoreIcon />}
                      onClick={e => {
                        e.stopPropagation();
                        void onRestoreMeeting(meeting.id);
                      }}
                      disabled={processingMeetingIds.has(meeting.id)}
                      fullWidth
                    >
                      Restore
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    );

    if (error) {
      return <Alert severity="error">Failed to load meetings: {error.message}</Alert>;
    }

    return (
      <Stack spacing={3}>
        {/* Create meeting button for organizers */}
        {isOrganizer && onCreateMeeting && (
          <Stack direction="row" justifyContent="flex-end">
            <Button variant="contained" startIcon={<AddIcon />} onClick={onCreateMeeting}>
              Schedule Meeting
            </Button>
          </Stack>
        )}

        {loading ? (
          <Typography color="text.secondary">Loading meetings...</Typography>
        ) : meetings.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No meetings scheduled
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isOrganizer
                ? 'Schedule your first meeting to get started!'
                : 'Check back later for upcoming meetings.'}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Upcoming meetings */}
            {upcomingMeetings.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Upcoming Meetings ({upcomingMeetings.length})
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={3} justifyContent="flex-start">
                  {upcomingMeetings.map(renderMeetingCard)}
                </Stack>
              </Box>
            )}

            {/* Past meetings */}
            {pastMeetings.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Past Meetings ({pastMeetings.length})
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={3} justifyContent="flex-start">
                  {pastMeetings.map(renderMeetingCard)}
                </Stack>
              </Box>
            )}

            {/* Canceled meetings (for organizers only) */}
            {isOrganizer && canceledMeetings.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Canceled Meetings ({canceledMeetings.length})
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={3} justifyContent="flex-start">
                  {canceledMeetings.map(renderMeetingCard)}
                </Stack>
              </Box>
            )}
          </>
        )}
      </Stack>
    );
  },
);

GroupMeetingsTab.displayName = 'GroupMeetingsTab';

export default GroupMeetingsTab;
